import { Alert, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useNavigate, useParams } from "react-router";

import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaPageComponent from "~/modules/app/components/GaiaPageComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import { RESEARCH_TYPE_LIST_PATH } from "~/modules/settings/constants";
import FormTemplateEditor from "~/modules/settings/shared/formTemplateEditor/components/FormTemplateEditor";
import { useSettingsFormTemplateEditorData } from "~/modules/settings/shared/formTemplateEditor/hooks/useSettingsFormTemplateEditorData";

import useSettingsResearchTypeEditRoute_GetById from "./api/useSettingsResearchTypeEditRoute_GetById";
import FunctionTypeDataGrid from "./components/FunctionTypeDataGrid";
import ObservationTypeDataGrid from "./components/ObservationTypeDataGrid";
import ResearchTypeSpeciesListCard from "./components/ResearchTypeSpeciesListCard";
import ResearchTypeUpdateForm, { ResearchTypeUpdateFormInputs } from "./components/ResearchTypeUpdateForm";
import VisitSummaryTemplateForm from "./components/VisitSummaryTemplateForm";
import { useSettingsResearchTypeData } from "./hooks/useSettingsResearchTypeData";
import { SettingsResearchTypeEditRoute_GetByIdDocument } from "./queries.generated";

export function ResearchTypeEditScreenBreadcrumb() {
    const { researchTypeId } = useParams();
    if (!researchTypeId) throw Error("no researchTypeId param in url");

    const { researchType } = useSettingsResearchTypeEditRoute_GetById(researchTypeId);
    return researchType?.name;
}

export default function SettingsResearchTypeEditRoute() {
    const { t } = useTranslation("researchType");

    const navigate = useNavigate();
    const { researchTypeId } = useParams();
    if (!researchTypeId) throw Error("no researchTypeId param in url");

    const {
        queryData,
        updateMutation,
        deleteMutation,
        addSpeciesMutation,
        updateSpeciesShowToClientUserMutation,
        removeSpeciesMutation,
        updateVisitSummaryTemplateMutation,
    } = useSettingsResearchTypeData(researchTypeId);

    const {
        addFormTemplateQuestionMutation,
        updateFormTemplateQuestionMutation,
        deleteFormTemplateQuestionMutation,
        handleAddQuestion,
        handleUpdateQuestion,
        editQuestionError,
    } = useSettingsFormTemplateEditorData({
        refetchQueries: [SettingsResearchTypeEditRoute_GetByIdDocument],
        formTemplateId: queryData.researchType?.submitForm?.id,
    });

    const handleSubmit = async (data: ResearchTypeUpdateFormInputs) => {
        await updateMutation.updateResearchType(researchTypeId, data);
        navigate(generatePath(RESEARCH_TYPE_LIST_PATH));
    };

    const handleDelete = async () => {
        await deleteMutation.deleteResearchType(researchTypeId);
        navigate(generatePath(RESEARCH_TYPE_LIST_PATH));
    };

    return (
        <GaiaPageLayoutColumn>
            {queryData.researchType && (
                <>
                    <GaiaPageComponent title={t("settingsResearchTypeEditRoute.title")} isLoading={queryData.loading}>
                        {queryData.error && <Alert severity="error">{queryData.error.message}</Alert>}

                        <ResearchTypeUpdateForm
                            initialData={queryData.researchType}
                            onSubmit={handleSubmit}
                            error={updateMutation.error}
                        />
                    </GaiaPageComponent>

                    <GaiaPageComponent title={t("settingsResearchTypeEditRoute.researchTypeSpeciesListTitle")} titleVariant="h3">
                        <ResearchTypeSpeciesListCard
                            researchTypeSpecies={queryData.researchType.speciesV2}
                            researchTypeId={researchTypeId}
                            onAddSpecies={data =>
                                addSpeciesMutation.addResearchTypeSpecies({
                                    showToClientUser: data.showToClientUser,
                                    speciesId: data.speciesId,
                                    researchTypeId,
                                })
                            }
                            onToggleShowToClientUser={(speciesId, value) => {
                                return updateSpeciesShowToClientUserMutation.updateSpeciesShowToClientUser({
                                    researchTypeId,
                                    speciesId,
                                    showToClientUser: value,
                                });
                            }}
                            addSpeciesError={addSpeciesMutation.error}
                            onRemoveSpecies={speciesId =>
                                removeSpeciesMutation.removeResearchTypeSpecies({ researchTypeId, speciesId })
                            }
                            removeSpeciesError={removeSpeciesMutation.error}
                        />
                    </GaiaPageComponent>
                    <GaiaPageComponent title={t("settingsResearchTypeEditRoute.formTemplateEditorTitle")}>
                        <FormTemplateEditor
                            formTemplateQuestions={queryData.researchType.submitForm.questions}
                            onAddQuestion={handleAddQuestion}
                            onUpdateQuestion={handleUpdateQuestion}
                            onDeleteQuestion={async id => await deleteFormTemplateQuestionMutation.deleteFormTemplateQuestion(id)}
                            isAddQuestionLoading={addFormTemplateQuestionMutation.loading}
                            isUpdateQuestionLoading={updateFormTemplateQuestionMutation.loading}
                            isDeleteQuestionLoading={deleteFormTemplateQuestionMutation.loading}
                            addQuestionError={addFormTemplateQuestionMutation.error}
                            editQuestionError={editQuestionError}
                        />
                    </GaiaPageComponent>

                    <GaiaPageComponent title={t("settingsResearchTypeEditRoute.observationTypeListTitle")} titleVariant="h3">
                        <Button component={Link} to="observationType/new">
                            {t("settingsResearchTypeEditRoute.btnCreateObservationType")}
                        </Button>
                        <ObservationTypeDataGrid observationTypes={queryData.researchType.observationTypes} />
                    </GaiaPageComponent>

                    <GaiaPageComponent title={t("settingsResearchTypeEditRoute.functionTypeListTitle")} titleVariant="h3">
                        <Button component={Link} to="functionType/new">
                            {t("settingsResearchTypeEditRoute.btnCreateFunctionType")}
                        </Button>
                        <FunctionTypeDataGrid
                            functionTypes={queryData.researchType.ecologicalFunctionTypes}
                            researchTypeId={researchTypeId}
                        />
                    </GaiaPageComponent>

                    <GaiaPageComponent title={t("settingsResearchTypeEditRoute.visitSummaryTemplateTitle")} titleVariant="h3">
                        <VisitSummaryTemplateForm
                            initialData={queryData.researchType}
                            onSubmit={template =>
                                updateVisitSummaryTemplateMutation.updateVisitSummaryTemplate(researchTypeId, template)
                            }
                            error={updateVisitSummaryTemplateMutation.error}
                        />
                    </GaiaPageComponent>

                    <GaiaPageComponent title={t("settingsResearchTypeEditRoute.dangerZone")} titleVariant="h3">
                        {deleteMutation.error && <Alert severity="error">{deleteMutation.error.message}</Alert>}
                        <GaiaDeleteButton
                            label={t("settingsResearchTypeEditRoute.researchTypeDeleteBtn")}
                            onDelete={handleDelete}
                            isLoading={deleteMutation.loading}
                        />
                    </GaiaPageComponent>
                </>
            )}
        </GaiaPageLayoutColumn>
    );
}

export interface SettingsResearchTypeSpeciesCreateInput {
    speciesId: string;
    showToClientUser: boolean;
}
